import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { t } from '../platform/i18n.js';
import { useLanguage } from '../platform/use-language.js';
import { useNavSheet, toggleNavSheet } from '../platform/nav-sheet.js';
import { useNavCounts } from '../platform/nav-counts-store.js';
import { useMaxWidth } from './use-max-width.js';
import { LABEL, Icon, type Destination } from './nav.js';

/**
 * THE AREA CHIP (nav redesign 10b) — the phone nav's whole title band. It IS each top-level
 * screen's title row now: the tab's title is the chip, and the screen's own title-row information
 * (a count, a date, a backlog) rides beside it as `children`. Tapping the chip opens the nav
 * sheet (see nav.tsx / nav-sheet.ts), which lists every destination.
 *
 * ⚠️ PHONE/TABLET ONLY IS A DISCLOSURE. At ≥1024px the nav is a permanent sidebar (styles.css), so
 * there is nothing to disclose — the chip renders as a plain heading. The breakpoint is the
 * SIDEBAR's 1024, not the screens' own 700 phone/desk split: between 700 and 1023 there is no
 * sidebar and no top bar, so the disclosure is the only way to switch area there.
 *
 * `AreaChip` wraps the pieces in an `<h2>` for the common `.screen-head` band. `items.tsx` has its
 * own bespoke `<h2>` (a measured sticky header) and cannot nest one, so it composes the same two
 * exported pieces — `AreaSwitch` and `AreaWaitingBadge` — directly.
 */

/**
 * THE DISCLOSURE CONTROL: the tappable chip on phone/tablet, a plain label at a desk.
 *
 * ⚠️ THE BUTTON'S ACCESSIBLE NAME IS "Switch area, current X", NOT "X". Deliberate and
 * load-bearing for the tests: a button named "Boxes" would collide with the nav destination button
 * of the same name under Playwright strict mode. The icon and caret are aria-hidden, so a wrapping
 * heading's accessible name stays the plain label and `getByRole('heading',{name})` still resolves.
 */
export function AreaSwitch({ area }: { area: Destination }): JSX.Element {
  useLanguage();
  const open = useNavSheet();
  const narrow = useMaxWidth(1023);
  const label = t(LABEL[area]);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Return focus to the chip when the sheet closes (Escape, scrim, a pick). The chip owns this
  // because it knows its own ref; the sheet in the shell does not.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open && narrow) btnRef.current?.focus();
    wasOpen.current = open;
  }, [open, narrow]);

  if (!narrow) return <span className="area-chip-label">{label}</span>;

  return (
    <button
      ref={btnRef}
      type="button"
      className="area-chip"
      aria-label={`Switch area, current ${label}`}
      aria-expanded={open}
      aria-controls="app-nav-sheet"
      aria-haspopup="menu"
      onClick={() => toggleNavSheet()}
    >
      <Icon name={area} />
      <span className="area-chip-label">{label}</span>
      <span className="area-chip-caret" aria-hidden="true">
        {open ? '⌃' : '⌄'}
      </span>
    </button>
  );
}

/**
 * THE BAND'S WAITING NUMBER (frame 10b): open maintenance jobs, the same count the sheet's
 * Maintenance row carries. Zero renders nothing — a permanent 0 is furniture.
 *
 * ⚠️ PHONE/TABLET ONLY, for two reasons that agree: 10b's waiting number is the phone nav band's
 * element (at a desk the permanent sidebar already carries the maintenance badge), AND a badge
 * inside the header <h2> would pollute the heading's accessible name at a desk — "Items 26" — which
 * the heading-by-name selectors must not see. It renders null at ≥1024px, so the desk heading is
 * just the title.
 */
export function AreaWaitingBadge(): JSX.Element | null {
  const narrow = useMaxWidth(1023);
  const counts = useNavCounts();
  const waiting = counts?.maintenanceOpen ?? 0;
  return narrow && waiting > 0 ? <span className="area-chip-waiting">{waiting}</span> : null;
}

export function AreaChip({
  area,
  children,
  className,
  trailing,
}: {
  /** The destination this screen IS — its title in the band and, tapped, the sheet's current row. */
  area: Destination;
  /** The screen's own count/date/backlog — sits beside the chip in the band. */
  children?: ReactNode;
  /** Extra class on the band, if a screen needs one. */
  className?: string;
  /** The band's RIGHT-EDGE control (frame 56: the phone Display pill) — the ONE control
   *  DESIGN.md rule 3 allows in the band, after the waiting badge. */
  trailing?: ReactNode;
}): JSX.Element {
  const bandClass = className === undefined ? 'area-band' : `area-band ${className}`;
  // ⚠️ THE BAND IS ITS OWN ROW — a thin `--surface` bar that IS the phone nav bar (10b), separate
  // from the screen's controls, which sit BELOW it on `--bg` (the maintainer's feedback: the band's
  // background must not spread onto the search/arrange/add controls). Layout: chip · count · spacer
  // · waiting — the count hugs the chip, the waiting badge is pushed to the far-right corner.
  //
  // ⚠️ THE COUNT AND WAITING ARE SIBLINGS OF THE <h2>, NOT INSIDE IT: nesting them made the
  // heading's accessible name "Retour 1 parcels" and broke every `getByRole('heading',{name,
  // exact})`. The heading stays the plain title.
  return (
    <div className={bandClass}>
      <h2 className="area-band-title">
        <AreaSwitch area={area} />
      </h2>
      {children}
      <span className="area-band-spacer" aria-hidden="true" />
      <AreaWaitingBadge />
      {trailing}
    </div>
  );
}
